import React, { useState } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeart, faGraduationCap, faHandHoldingHeart, faShareAlt, faCheck } from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';

const SectionWrapper = styled.section`
  padding: 5rem 0;
  position: relative;
  background: linear-gradient(135deg, #1a1a1a 0%, #2d0006 50%, #1a1a1a 100%);
  color: white;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle at center, rgba(153, 0, 17, 0.15) 0%, transparent 60%);
    pointer-events: none;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 2;

  @media (max-width: 768px) {
    padding: 0 1.25rem;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const Badge = styled.span`
  display: inline-block;
  padding: 0.5rem 1.25rem;
  background: rgba(153, 0, 17, 0.25);
  border: 1px solid rgba(153, 0, 17, 0.5);
  color: #ff4d6d;
  border-radius: 50px;
  font-size: 0.875rem;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  margin-bottom: 1rem;
`;

const Title = styled.h2`
  font-size: clamp(2rem, 4vw, 3.2rem);
  font-weight: 800;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #ffffff 0%, #f0f0f0 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;

  span {
    color: ${props => props.theme.colors.primary || '#990011'};
    -webkit-text-fill-color: ${props => props.theme.colors.primary || '#990011'};
  }
`;

const Subtitle = styled.p`
  font-size: clamp(1rem, 1.8vw, 1.25rem);
  color: rgba(255, 255, 255, 0.85);
  max-width: 720px;
  margin: 0 auto;
  line-height: 1.6;
`;

const VideoCard = styled.div`
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(12px);
  border-radius: 24px;
  padding: 1.5rem;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.5);
  margin-bottom: 3rem;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    box-shadow: 0 25px 60px rgba(153, 0, 17, 0.25);
  }

  @media (max-width: 768px) {
    padding: 0.75rem;
    border-radius: 16px;
  }
`;

const AspectRatioContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 440px;
  margin: 0 auto;
  aspect-ratio: 9 / 16;
  border-radius: 20px;
  overflow: hidden;
  background: #000;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.6);

  @supports not (aspect-ratio: 9 / 16) {
    padding-top: 177.78%; /* 9:16 Fallback */
  }

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: none;
  }

  @media (max-width: 480px) {
    max-width: 100%;
    border-radius: 16px;
  }
`;

const HighlightsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  margin-top: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const HighlightCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 1.5rem;
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    transform: translateY(-4px);
    border-color: rgba(153, 0, 17, 0.4);
  }
`;

const IconCircle = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary || '#990011'}, #66000b);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  color: white;
  flex-shrink: 0;
`;

const HighlightText = styled.div`
  h4 {
    font-size: 1.1rem;
    font-weight: 700;
    margin-bottom: 0.35rem;
    color: white;
  }

  p {
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.7);
    line-height: 1.5;
  }
`;

const ActionsContainer = styled.div`
  display: flex;
  gap: 1.25rem;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 2.5rem;
`;

const PrimaryCTA = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary || '#990011'}, #cc0017);
  color: white;
  padding: 1rem 2.25rem;
  border-radius: 50px;
  font-weight: 700;
  font-size: 1.05rem;
  text-decoration: none;
  box-shadow: 0 8px 20px rgba(153, 0, 17, 0.4);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 25px rgba(153, 0, 17, 0.6);
    background: linear-gradient(135deg, #b30014, #e6001a);
    color: white;
  }
`;

const SecondaryCTA = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  padding: 1rem 2.25rem;
  border-radius: 50px;
  font-weight: 600;
  font-size: 1.05rem;
  text-decoration: none;
  border: 1px solid rgba(255, 255, 255, 0.25);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    background: rgba(255, 255, 255, 0.2);
    color: white;
  }
`;

const ShareButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: transparent;
  color: rgba(255, 255, 255, 0.8);
  padding: 0.8rem 1.5rem;
  border-radius: 50px;
  font-weight: 600;
  font-size: 0.95rem;
  border: 1px dashed rgba(255, 255, 255, 0.3);
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    color: white;
    border-color: rgba(255, 255, 255, 0.7);
    background: rgba(255, 255, 255, 0.05);
  }
`;

const ProjectVideo = ({ isStandalone = false }) => {
  const [copied, setCopied] = useState(false);

  const handleShare = () => {
    const shareUrl = window.location.origin + '/project-video';
    if (navigator.clipboard) {
      navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    }
  };

  return (
    <SectionWrapper id="project-video">
      <Container>
        <Header>
          <Badge>Featured Project Documentary</Badge>
          <Title>Watch <span>The Big Heart</span> Story</Title>
          <Subtitle>
            Discover our journey across Morocco, our impact on education and healthcare, and how we are renovating schools for the next generation.
          </Subtitle>
        </Header>

        <VideoCard>
          <AspectRatioContainer>
            <iframe
              src="https://player.vimeo.com/video/1217931536?badge=0&autopause=0&player_id=0&app_id=58479"
              title="The Big Heart Association Project Overview"
              allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
              allowFullScreen
            ></iframe>
          </AspectRatioContainer>

          <HighlightsGrid>
            <HighlightCard>
              <IconCircle>
                <FontAwesomeIcon icon={faGraduationCap} />
              </IconCircle>
              <HighlightText>
                <h4>Heartbeat Journey</h4>
                <p>Renovating 24 rural schools across Morocco's 12 regions to empower thousands of students.</p>
              </HighlightText>
            </HighlightCard>

            <HighlightCard>
              <IconCircle>
                <FontAwesomeIcon icon={faHandHoldingHeart} />
              </IconCircle>
              <HighlightText>
                <h4>Medical Caravans</h4>
                <p>Bringing essential healthcare, medical supplies, and humanitarian assistance to remote villages.</p>
              </HighlightText>
            </HighlightCard>

            <HighlightCard>
              <IconCircle>
                <FontAwesomeIcon icon={faHeart} />
              </IconCircle>
              <HighlightText>
                <h4>Youth-Driven Impact</h4>
                <p>Powered by over 70 dedicated volunteers united by compassion and sustainable development.</p>
              </HighlightText>
            </HighlightCard>
          </HighlightsGrid>

          <ActionsContainer>
            <PrimaryCTA to="/donate">
              <FontAwesomeIcon icon={faHeart} /> Support Our Mission
            </PrimaryCTA>
            
            <SecondaryCTA to="/heartbeat-journey">
              Explore Heartbeat Journey
            </SecondaryCTA>

            <ShareButton onClick={handleShare}>
              <FontAwesomeIcon icon={copied ? faCheck : faShareAlt} />
              {copied ? 'Link Copied!' : 'Share Video'}
            </ShareButton>
          </ActionsContainer>
        </VideoCard>
      </Container>
    </SectionWrapper>
  );
};

export default ProjectVideo;
